import { List, makeStyles, Paper } from '@material-ui/core';
import React, { useContext, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../context/Auth/AuthContext';
import { useSocket } from '../../context/SocketContext';
import toastError from '../../errors/toastError';
import useTickets from '../../hooks/useTickets';
import api from '../../services/api';
import TagsFilter from '../TagsFilter';
import TicketListItem from '../TicketListItem';
import TicketsListSkeleton from '../TicketsListSkeleton';

const useStyles = makeStyles((theme) => ({
  ticketsListWrapper: {
    position: 'relative',
    display: 'flex',
    height: '100%',
    flexDirection: 'column',
    overflow: 'hidden',
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  ticketsList: {
    flex: 1,
    overflowY: 'scroll',
    ...theme.scrollbarStyles,
    borderTop: '2px solid rgba(0, 0, 0, 0.12)',
  },
  ticketsListHeader: {
    color: 'rgb(67, 83, 105)',
    zIndex: 2,
    backgroundColor: 'white',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  ticketsCount: {
    fontWeight: 'normal',
    color: 'rgb(104, 121, 146)',
    marginLeft: '8px',
    fontSize: '14px',
  },
  noTicketsText: {
    textAlign: 'center',
    color: 'rgb(104, 121, 146)',
    fontSize: '14px',
    lineHeight: '1.4',
  },
  noTicketsTitle: {
    textAlign: 'center',
    fontSize: '16px',
    fontWeight: '600',
    margin: '0px',
  },
  noTicketsDiv: {
    display: 'flex',
    height: '100px',
    margin: 40,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const reducer = (state, action) => {
  if (action.type === 'LOAD_TICKETS') {
    const newTickets = action.payload;

    newTickets.forEach((ticket) => {
      const ticketIndex = state.findIndex((t) => t.id === ticket.id);
      if (ticketIndex !== -1) {
        state[ticketIndex] = ticket;
        if (ticket.unreadMessages > 0) {
          state.unshift(state.splice(ticketIndex, 1)[0]);
        }
      } else {
        state.push(ticket);
      }
    });
    console.log([...state]);
    return [...state];
  }

  if (action.type === 'RESET_UNREAD') {
    const ticketId = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state[ticketIndex].unreadMessages = 0;
    }

    return [...state];
  }

  if (action.type === 'UPDATE_TICKET') {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      state[ticketIndex] = ticket;
    } else {
      state.unshift(ticket);
    }

    return [...state];
  }

  if (action.type === 'UPDATE_TICKET_UNREAD_MESSAGES') {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      state[ticketIndex] = ticket;
      state.unshift(state.splice(ticketIndex, 1)[0]);
    } else {
      state.unshift(ticket);
    }

    return [...state];
  }

  if (action.type === 'UPDATE_TICKET_CONTACT') {
    const contact = action.payload;
    const ticketIndex = state.findIndex((t) => t.contactId === contact.id);
    if (ticketIndex !== -1) {
      state[ticketIndex].contact = contact;
    }
    return [...state];
  }

  if (action.type === 'DELETE_TICKET') {
    const ticketId = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state.splice(ticketIndex, 1);
    }

    return [...state];
  }

  if (action.type === 'RESET') {
    return [];
  }
};

const TicketsList = (props) => {
  const {
    status,
    searchParam,
    showAll,
    selectedQueueIds,
    updateCount,
    style,
    tags,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const [pageNumber, setPageNumber] = useState(1);
  const [ticketsList, dispatch] = useReducer(reducer, []);
  const { user } = useContext(AuthContext);
  const { profile, queues } = user;
  const [settings, setSettings] = useState([]);
  const [filteredTags, setFilteredTags] = useState([]);

  const { socket } = useSocket();

  const handleTagFilter = (tags) => {
    setFilteredTags(tags);
  };

  useEffect(() => {
    dispatch({ type: 'RESET' });
    setPageNumber(1);
  }, [status, searchParam, dispatch, showAll, selectedQueueIds, tags]);

  const { tickets, hasMore, loading } = useTickets({
    pageNumber,
    searchParam,
    status,
    showAll,
    tags: JSON.stringify(tags),
    queueIds: JSON.stringify(selectedQueueIds),
  });

  useEffect(() => {
    if (!socket) {
      return undefined;
    }

    const handleReconnectSync = () => {
      setPageNumber(1);
    };

    socket.on('connect', handleReconnectSync);
    socket.on('reconnect', handleReconnectSync);

    return () => {
      socket.off('connect', handleReconnectSync);
      socket.off('reconnect', handleReconnectSync);
    };
  }, [socket]);

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const { data } = await api.get('/settings');
        setSettings(data);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, []);

  useEffect(() => {
    const queueIds = queues.map((q) => q.id);

    const getSettingValue = (key) => {
      const setting = settings.find((s) => s.key === key);
      return setting ? setting.value : null;
    };

    const allticket =
      settings &&
      settings.length > 0 &&
      getSettingValue('allTicket') === 'enabled';

    if (allticket === true) {
      if (profile === '') {
        const filteredTickets = tickets.filter(
          (t) => queueIds.indexOf(t.queueId) > -1
        );
        dispatch({ type: 'LOAD_TICKETS', payload: filteredTickets });
      } else {
        dispatch({ type: 'LOAD_TICKETS', payload: tickets });
      }
    } else {
      if (profile === 'user') {
        const filteredTickets = tickets.filter(
          (t) => queueIds.indexOf(t.queueId) > -1
        );
        dispatch({ type: 'LOAD_TICKETS', payload: filteredTickets });
      } else {
        dispatch({ type: 'LOAD_TICKETS', payload: tickets });
      }
    }
  }, [tickets, status, searchParam, queues, profile, settings]);

  useEffect(() => {
    if (!socket) {
      return;
    }

    const normalizedProfile = (user?.profile || '').toLowerCase();
    const isAdmin = normalizedProfile === 'admin' || normalizedProfile === 'masteradmin';

    const canSeeTicket = (ticket) => {
      if (!ticket) return false;

      if (isAdmin) {
        if (showAll) return true;
        return !ticket.queueId || selectedQueueIds.indexOf(ticket.queueId) > -1;
      }

      if (ticket.userId) {
        return Number(ticket.userId) === Number(user?.id);
      }

      if (!ticket.queueId) {
        return false;
      }

      return selectedQueueIds.indexOf(ticket.queueId) > -1;
    };

    const belongsToCurrentStatus = (ticket) => {
      if (!status) return true;
      return ticket?.status === status;
    };

    const joinRooms = () => {
      if (status) {
        socket.emit('joinTickets', status);
      } else {
        socket.emit('joinNotification');
      }
    };

    const handleTicketUpdate = (data) => {
      if (data.action === 'updateUnread') {
        dispatch({
          type: 'RESET_UNREAD',
          payload: data.ticketId,
        });
        return;
      }

      if (data.action === 'update') {
        if (canSeeTicket(data.ticket) && belongsToCurrentStatus(data.ticket)) {
          dispatch({
            type: 'UPDATE_TICKET',
            payload: data.ticket,
          });
        } else {
          dispatch({ type: 'DELETE_TICKET', payload: data.ticket.id });
        }
        return;
      }

      if (data.action === 'delete') {
        dispatch({ type: 'DELETE_TICKET', payload: data.ticketId });
      }
    };

    const handleAppMessage = (data) => {

      if (
        data.action === 'create' &&
        canSeeTicket(data.ticket) &&
        belongsToCurrentStatus(data.ticket)
      ) {
        dispatch({
          type: 'UPDATE_TICKET_UNREAD_MESSAGES',
          payload: data.ticket,
        });
      } else if (data.action === 'create' && data.ticket?.id) {
        dispatch({ type: 'DELETE_TICKET', payload: data.ticket.id });
      }
    };

    const handleContactUpdate = (data) => {

      if (data.action === 'update') {
        dispatch({
          type: 'UPDATE_TICKET_CONTACT',
          payload: data.contact,
        });
      }
    };

    if (socket.connected) {
      joinRooms();
    }

    const handleConnect = () => {
      joinRooms();
    };

    socket.on('connect', handleConnect);

    socket.on('ticket', handleTicketUpdate);
    socket.on('appMessage', handleAppMessage);
    socket.on('contact', handleContactUpdate);

    return () => {
      if (status) {
        socket.emit('leaveTickets', status);
      } else {
        socket.emit('leaveNotification');
      }
      socket.off('connect', handleConnect);
      socket.off('ticket', handleTicketUpdate);
      socket.off('appMessage', handleAppMessage);
      socket.off('contact', handleContactUpdate);
    };
  }, [socket, status, showAll, user, selectedQueueIds]);

  useEffect(() => {
    if (typeof updateCount === 'function') {
      updateCount(ticketsList.length);
    }
  }, [ticketsList, updateCount]);

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <Paper className={classes.ticketsListWrapper} style={style}>
      <TagsFilter onFiltered={handleTagFilter} />
      <Paper
        square
        name="closed"
        elevation={0}
        className={classes.ticketsList}
        onScroll={handleScroll}
      >
        <List style={{ paddingTop: 0 }}>
          {ticketsList.length === 0 && !loading ? (
            <div className={classes.noTicketsDiv}>
              <span className={classes.noTicketsTitle}>
                {t('ticketsList.noTicketsTitle')}
              </span>
              <p className={classes.noTicketsText}>
                {t('ticketsList.noTicketsMessage')}
              </p>
            </div>
          ) : (
            <>
              {ticketsList.map((ticket) => (
                <TicketListItem
                  key={ticket.id}
                  ticket={ticket}
                  filteredTags={filteredTags}
                />
              ))}
            </>
          )}
          {loading && <TicketsListSkeleton />}
        </List>
      </Paper>
    </Paper>
  );
};

export default TicketsList;
